import React, { useState, useEffect } from 'react';
import { useAuth } from '../../components/hooks/useAuth';
import { getAllUsers, deleteUser, getUserStatistics } from '../../services/supabaseService';
import UserTable from './components/UserTable';
import UserFormModal from './components/UserFormModal';
import InviteUserModal from './components/InviteUserModal';
import BulkRoleModal from './components/BulkRoleModal';
import CenterAllocationModal from './components/CenterAllocationModal';
import UserMetrics from './components/UserMetrics';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { getInvitations, cancelInvitation, resendInvitation } from '../../services/invitationService';

const UserManagement = () => {
  const { userProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'invitations'
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isBulkRoleModalOpen, setIsBulkRoleModalOpen] = useState(false);
  const [isCenterAllocationModalOpen, setIsCenterAllocationModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    if (['super_admin', 'admin']?.includes(userProfile?.role)) {
      fetchUsers();
      fetchInvitations();
      fetchStatistics();
    }
  }, [userProfile]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = {
        search: searchTerm || undefined,
        role: roleFilter !== 'all' ? roleFilter : undefined,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
      };
      const data = await getAllUsers(filters);
      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    try {
      const result = await getInvitations();
      if (result?.success) {
        setInvitations(result?.data || []);
      }
    } catch (err) {
      console.error('Error fetching invitations:', err);
    }
  };

  const fetchStatistics = async () => {
    try {
      const stats = await getUserStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const handleSearch = () => {
    fetchUsers();
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsFormModalOpen(true);
  };

  const handleInviteUser = () => {
    setIsInviteModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsFormModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user? They will no longer be able to access the system.')) {
      return;
    }

    try {
      await deleteUser(userId);
      fetchUsers();
      fetchStatistics();
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(err?.message || 'Failed to delete user');
    }
  };

  const handleSelectUser = (userId, checked) => {
    setSelectedUsers((prev) =>
      checked ? [...prev, userId] : prev?.filter((id) => id !== userId)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedUsers(checked ? users?.map((u) => u?.id) : []);
  };

  const handleBulkRoleAssignment = () => {
    if (selectedUsers?.length === 0) {
      alert('Please select at least one user');
      return;
    }
    setIsBulkRoleModalOpen(true);
  };

  const handleAllocateCenters = (user) => {
    setSelectedUser(user);
    setIsCenterAllocationModalOpen(true);
  };

  const handleCenterAllocationSuccess = () => {
    setIsCenterAllocationModalOpen(false);
    setSelectedUser(null);
    fetchUsers();
  };

  const handleFormSuccess = () => {
    setIsFormModalOpen(false);
    setSelectedUser(null);
    fetchUsers();
    fetchStatistics();
  };

  const handleInviteSuccess = () => {
    setIsInviteModalOpen(false);
    fetchInvitations();
    fetchStatistics();
  };

  const handleBulkRoleSuccess = () => {
    setIsBulkRoleModalOpen(false);
    setSelectedUsers([]);
    fetchUsers();
    fetchStatistics();
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      const result = await cancelInvitation(invitationId);
      if (result?.success) {
        fetchInvitations();
      } else {
        alert(result?.error || 'Failed to cancel invitation');
      }
    } catch (err) {
      console.error('Error cancelling invitation:', err);
      alert('Failed to cancel invitation');
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      const result = await resendInvitation(invitationId);
      if (result?.success) {
        alert('Invitation email resent successfully');
      } else {
        alert(result?.error || 'Failed to resend invitation');
      }
    } catch (err) {
      console.error('Error resending invitation:', err);
      alert('Failed to resend invitation');
    }
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      'super_admin': 'Super Admin',
      'admin': 'Admin/Owner',
      'center_manager': 'Center Manager Sales',
      'center_manager_ops': 'Center Manager Ops',
      'sales': 'Sales'
    };
    return roleLabels?.[role] || role;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      'accepted': { color: 'bg-green-100 text-green-800', label: 'Accepted' },
      'expired': { color: 'bg-gray-100 text-gray-800', label: 'Expired' },
      'cancelled': { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };
    const config = statusConfig?.[status] || statusConfig?.['pending'];
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  if (!['super_admin', 'admin']?.includes(userProfile?.role)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Icon name="Lock" size={48} className="mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600">Only administrators can access User Management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-1">Manage users, roles, and permissions</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleInviteUser} className="flex items-center gap-2">
            <Icon name="Mail" size={16} />
            Invite User
          </Button>
          <Button onClick={handleAddUser} className="flex items-center gap-2">
            <Icon name="UserPlus" size={16} />
            Add User
          </Button>
        </div>
      </div>

      {/* Metrics */}
      {statistics && <UserMetrics statistics={statistics} />}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'users' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon name="Users" size={16} />
              Active Users
              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                {users?.length || 0}
              </span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('invitations')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'invitations' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon name="Mail" size={16} />
              Invitations
              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                {invitations?.filter(inv => inv?.status === 'pending')?.length || 0}
              </span>
            </div>
          </button>
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && handleSearch()}
                  className="w-full"
                />
              </div>
              <div className="w-full md:w-48">
                <Select
                  value={roleFilter}
                  onChange={(e) => {
                    setRoleFilter(e?.target?.value);
                  }}
                >
                  <option value="all">All Roles</option>
                  {userProfile?.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
                  <option value="admin">Admin/Owner</option>
                  <option value="center_manager">Center Manager Sales</option>
                  <option value="center_manager_ops">Center Manager Ops</option>
                  <option value="sales">Sales</option>
                </Select>
              </div>
              <div className="w-full md:w-48">
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e?.target?.value);
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>
              </div>
              <Button onClick={handleSearch} variant="primary">
                <Icon name="Search" size={20} />
              </Button>
            </div>
          </div>

          {/* User Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          ) : (
            <UserTable
              users={users}
              selectedUsers={selectedUsers}
              onSelectUser={handleSelectUser}
              onSelectAll={handleSelectAll}
              onEdit={handleEditUser}
              onDelete={handleDeleteUser}
              onAllocateCenters={handleAllocateCenters}
              currentUserRole={userProfile?.role}
            />
          )}
        </>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Centers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invited By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expires At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitations?.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <Icon name="Mail" size={48} className="mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-500">No invitations found</p>
                      <Button onClick={handleInviteUser} className="mt-4">
                        Send First Invitation
                      </Button>
                    </td>
                  </tr>
                ) : (
                  invitations?.map((invitation) => (
                    <tr key={invitation?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Icon name="Mail" size={16} className="text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{invitation?.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{getRoleLabel(invitation?.invitedRole)}</span>
                      </td>
                      <td className="px-6 py-4">
                        {invitation?.invitationCenterAllocations?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {invitation?.invitationCenterAllocations?.map((alloc) => (
                              <span
                                key={alloc?.centerId}
                                className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
                              >
                                {alloc?.centers?.name || alloc?.centerId}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No centers</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(invitation?.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {invitation?.invitedByProfile?.fullName || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {new Date(invitation?.expiresAt)?.toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {invitation?.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleResendInvitation(invitation?.id)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Resend invitation"
                              >
                                <Icon name="RefreshCw" size={16} />
                              </button>
                              <button
                                onClick={() => handleCancelInvitation(invitation?.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Cancel invitation"
                              >
                                <Icon name="X" size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      {isFormModalOpen && (
        <UserFormModal
          user={selectedUser}
          onClose={() => setIsFormModalOpen(false)}
          onSuccess={handleFormSuccess}
        />
      )}

      {isInviteModalOpen && (
        <InviteUserModal
          onClose={() => setIsInviteModalOpen(false)}
          onSuccess={handleInviteSuccess}
        />
      )}

      {isBulkRoleModalOpen && (
        <BulkRoleModal
          userIds={selectedUsers}
          onClose={() => setIsBulkRoleModalOpen(false)}
          onSuccess={handleBulkRoleSuccess}
        />
      )}
      {isCenterAllocationModalOpen && (
        <CenterAllocationModal
          user={selectedUser}
          onClose={() => setIsCenterAllocationModalOpen(false)}
          onSuccess={handleCenterAllocationSuccess}
        />
      )}
    </div>
  );
};

export default UserManagement;